<script setup lang="ts">
    import favicon from '@/public/favicon.ico';

    const countryLanguages = useCountryLanguages();
    const { lang, country } = useLanguage();
    const route = useRoute();
    // Behind the ALB the request host is the internal one, so absolute URLs
    // must be derived from the configured public origin instead.
    const baseUrl = useRuntimeConfig().public.baseUrl;

    const usercentricsSettingsId = 'mC4lIc80nFJ0yE';

    const head = computed(() => {
        const alternates =
            countryLanguages[country.value]?.availableLanguages.map((langItem) => {
                const newPath = route.path.split('/').filter(Boolean);
                newPath[0] = `${country.value}-${langItem}`;
                return {
                    hreflang: getI18nLocale(country.value, langItem),
                    href: `${baseUrl}/${newPath.join('/')}`
                };
            }) || [];

        return {
            link: [
                { rel: 'icon' as const, type: 'image/x-icon', href: favicon },
                ...alternates.map((alt) => ({
                    rel: 'alternate' as const,
                    hreflang: alt.hreflang,
                    href: alt.href
                }))
            ],
            script: [
                {
                    key: 'usercentrics-cmp',
                    id: 'usercentrics-cmp',
                    src: 'https://web.cmp.usercentrics.eu/ui/loader.js',
                    'data-settings-id': usercentricsSettingsId,
                    async: true
                }
            ]
        };
    });

    useHead(head);

    // Shopware session storage logout cart
    let preLogoutCart = null;
    if (import.meta.client) {
        const value = sessionStorage.getItem('shopware-preLogoutCard');
        if (value) {
            preLogoutCart = JSON.parse(value);
        }
    }
    if (preLogoutCart) {
        const { addProducts } = useCart();
        await addProducts(preLogoutCart);
        sessionStorage.removeItem('shopware-preLogoutCard');
    }

    // register pre login hook
    if (import.meta.client) {
        const { register } = usePreLoginHooks();

        register(() => {
            snapshotCartBeforeLogin();
        });

        register(() => {
            setPTAFamilyLoginFlag();
        });
    }

    const { availableLocales, defaultLocale, locale, setLocale } = useI18n();
    const resolveTargetLocale = (nextLang: string) => {
        return availableLocales.find((availableLocale) => availableLocale === nextLang) ?? defaultLocale;
    };

    // Keep SSR deterministic: top-level await is awaited by Nuxt before initial render.
    const initialTargetLocale = resolveTargetLocale(lang.value);
    if (locale.value !== initialTargetLocale) {
        await setLocale(initialTargetLocale);
    }

    watch(lang, (nextLang) => {
        const targetLocale = resolveTargetLocale(nextLang);

        if (locale.value !== targetLocale) {
            void setLocale(targetLocale);
        }
    });

    useNotifications();
</script>

<template>
    <PageLoadingIndicator />

    <NuxtLayout>
        <NuxtPage />
    </NuxtLayout>
</template>

<style>
    select {
        background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIGZpbGw9J25vbmUnIHZpZXdCb3g9JzAgMCAyMCAyMCc+PHBhdGggc3Ryb2tlPScjNmI3MjgwJyBzdHJva2UtbGluZWNhcD0ncm91bmQnIHN0cm9rZS1saW5lam9pbj0ncm91bmQnIHN0cm9rZS13aWR0aD0nMS41JyBkPSdNNiA4bDQgNCA0LTQnLz48L3N2Zz4=');
        background-position: right 0.5rem center;
        background-repeat: no-repeat;
        background-size: 1.5em 1.5em;
        appearance: none;
    }
</style>
